import { FloatingFormItem } from '@/components/_common';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { CompanyType } from '@/core/domain/enterprise/Companies/Companies.types';
import { usePlanAccountCreateList } from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.hooks';
import {
  PlanAccountImport,
  PlanAccountImportValues,
  PlanAccountSelected,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import { planAccountBodyWorkMock } from '@/core/domain/enterprise/PlanAccounts/PlanAccountsBodyWork';
import { planAccountWorkShopMock } from '@/core/domain/enterprise/PlanAccounts/PlanAccountsWorkShop';
import { useDefaultEmptyFieldError } from '@/hooks';
import {
  Divider,
  Empty,
  Form,
  Modal,
  ModalProps,
  Select,
  Table,
  notification,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useState } from 'react';
import { useIntl } from 'react-intl';

interface Props extends Omit<ModalProps, 'onCancel'> {
  onCancel: VoidFunction;
}

interface FormValues {
  type: string;
}

export default function PlanAccountImportModal({ onCancel, ...props }: Props) {
  const intl = useIntl();
  const [form] = Form.useForm<FormValues>();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const [selectedPlanAccounts, setSelectedPlanAccounts] =
    useState<PlanAccountSelected[]>();

  const mutation = usePlanAccountCreateList();

  const type = Form.useWatch('type', form);

  function handleCancel() {
    form.resetFields();
    setSelectedPlanAccounts([]);

    onCancel();
  }

  async function handleCreate() {
    if (selectedPlanAccounts?.length) {
      const obj = selectedPlanAccounts.reduce(
        (acc: PlanAccountImportValues[], obj) => {
          const isParentAdded = acc.some(
            (item) => item.code === obj.parent?.code,
          );

          if (obj.parent && !isParentAdded) {
            acc.push({
              key: obj.parent.code,
              code: obj.parent.code,
              description: obj.parent.description,
              hierarchy: obj.parent.hierarchy,
              active: true,
              destination: obj.parent.destination,
              type: obj.parent.type,
              classification: obj.parent.classification,
            });
          }

          acc.push({
            key: obj.code,
            code: obj.code,
            description: obj.description,
            hierarchy: obj.hierarchy,
            destination: obj.destination,
            active: true,
            type: obj.type,
            classification: obj.classification,
          });

          return acc;
        },
        [],
      );

      await mutation.mutateAsync({ planAccountRequestDTOs: obj });

      handleCancel();
    } else {
      notification.error({ message: 'Informe ao menos 1 plano de contas' });
    }
  }

  const columns: ColumnsType<PlanAccountImport> = [
    { dataIndex: 'code', title: 'Código', width: 100 },
    { dataIndex: 'description', title: 'Descrição', width: 100 },
    {
      title: intl.formatMessage({ id: 'planAccounts.domain.hierarchy' }),
      dataIndex: 'hierarchy',
      key: 'hierarchy',
      render: (value) =>
        value
          ? intl.formatMessage({
              id: `planAccounts.modal.general.form.hierarchy.${value?.toLowerCase()}`,
            })
          : '',
      width: 100,
    },
    {
      title: intl.formatMessage({ id: 'planAccounts.domain.destination' }),
      dataIndex: 'destination',
      key: 'destination',
      render: (value) =>
        value
          ? intl.formatMessage({
              id: `planAccounts.modal.general.form.destination.${value?.toLowerCase()}`,
            })
          : '',
      width: 100,
    },
    {
      title: intl.formatMessage({
        id: 'planAccounts.modal.general.form.classification',
      }),
      dataIndex: 'classification',
      key: 'classification',
      render: (value) =>
        value
          ? intl.formatMessage({
              id: `planAccounts.modal.general.form.classification.${value?.toLowerCase()}`,
            })
          : '',
      width: 100,
    },
  ];

  const data =
    type === CompanyType.Funilaria
      ? planAccountBodyWorkMock
      : planAccountWorkShopMock;

  const importType = {
    [CompanyType.Funilaria]: 'Funilaria',
    [CompanyType.Mecanica]: 'Mecânica',
  };

  return (
    <Modal
      {...props}
      title={`Importação - Plano de Contas ${importType[type as CompanyType]}`}
      onOk={handleCreate}
      width={900}
      onCancel={handleCancel}
      okText="Importar"
      confirmLoading={mutation.isLoading}
    >
      {mutation.isLoading && (
        <ModalLoadingOverlay messages={['Criando plano de contas...']} />
      )}

      <Form form={form}>
        <FloatingFormItem
          label={intl.formatMessage({
            id: 'register.form.general.companyType.label',
          })}
          name="type"
          rules={[defaultEmptyFieldError]}
        >
          <Select>
            <Select.Option value="MECANICA">Mecânica</Select.Option>
            <Select.Option value="FUNILARIA">Funilaria</Select.Option>
          </Select>
        </FloatingFormItem>
      </Form>

      <Divider />

      {!!type ? (
        <Table<PlanAccountImport>
          columns={columns}
          dataSource={data.map(({ subPlanAccounts, ...item }) => ({
            ...item,
            key: item.code,
            children: subPlanAccounts?.map((subPlanAccount) => ({
              key: subPlanAccount.code,
              parent: item,
              ...subPlanAccount,
            })),
          }))}
          pagination={false}
          rowSelection={{
            checkStrictly: false,
            preserveSelectedRowKeys: true,
            selectedRowKeys: selectedPlanAccounts?.map((item) => item.code),
            onChange: (_, row) =>
              setSelectedPlanAccounts(row as PlanAccountSelected[]),
          }}
        />
      ) : (
        <Empty description="Selecione um tipo" />
      )}
    </Modal>
  );
}
